import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import truckDocumentService from '../services/truckDocumentService';
import apiClient from '../api';

const HIGH_CONFIDENCE = 0.75;
const ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.zip';

function statusColor(status) {
  switch (status) {
    case 'ready': return 'success';
    case 'needs_review': return 'warning';
    case 'filed': return 'info';
    case 'failed': return 'error';
    case 'pending': return 'default';
    default: return 'default';
  }
}

function confidenceLabel(c) {
  if (c == null) return '—';
  return `${Math.round(Number(c) * 100)}%`;
}

export default function TruckDocSmartUploadDialog({ open, onClose, trucks = [], onFiled }) {
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  const [step, setStep] = useState('upload'); // upload | processing | review
  const [batchId, setBatchId] = useState(null);
  const [items, setItems] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [infoMsg, setInfoMsg] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const truckOptions = useMemo(() => {
    const map = new Map();
    const isRetired = (unit) => /\b(old|deactivated|retired|inactive|previously\s+paired|do\s*not\s*use)\b/i.test(String(unit || ''))
      || /^old[\s_-]*\d/i.test(String(unit || ''))
      || /^vehicle\s+\d+/i.test(String(unit || '').trim());
    (trucks || []).forEach((t) => {
      if (!t?._id) return;
      if (isRetired(t.unitNumber)) return;
      map.set(String(t._id), t);
    });
    // Ensure AI-matched trucks still appear even if parent list is filtered/stale
    (items || []).forEach((row) => {
      const id = row.truck || row.suggestedTruck;
      if (!id) return;
      const key = String(id);
      if (map.has(key)) return;
      const unit = row.suggestedUnitNumber
        || (row.suggestedTruckLabel ? String(row.suggestedTruckLabel).replace(/^#/, '') : '');
      // Never inject retired / placeholder suggestions into the picker
      if (isRetired(unit)) return;
      map.set(key, {
        _id: key,
        unitNumber: unit || key.slice(-6),
        make: '',
      });
    });
    return [...map.values()].sort((a, b) =>
      String(a.unitNumber).localeCompare(String(b.unitNumber), undefined, { numeric: true })
    );
  }, [trucks, items]);

  const reset = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setStep('upload');
    setBatchId(null);
    setItems([]);
    setSelected(new Set());
    setUploading(false);
    setConfirming(false);
    setError(null);
    setSuccessMsg(null);
    setInfoMsg(null);
    setDragOver(false);
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      return undefined;
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open, reset]);

  const applyBatchPayload = useCallback((data) => {
    const list = data?.items || [];
    const withIssues = list.filter((row) => row.error || (row.aiDebug && row.status === 'failed'));
    if (withIssues.length) {
      console.warn('[SmartUpload] classification issues', withIssues.map((row) => ({
        file: row.originalFilename,
        status: row.status,
        error: row.error,
        aiDebug: row.aiDebug,
      })));
    }

    setItems(list.map((row) => {
      const isRetiredUnit = (unit) => /\b(old|deactivated|retired|inactive|previously\s+paired|do\s*not\s*use)\b/i.test(String(unit || ''))
        || /^old[\s_-]*\d/i.test(String(unit || ''))
        || /^vehicle\s+\d+/i.test(String(unit || '').trim());
      let truckId = row.truck
        || (row.suggestedTruck && (row.suggestedTruck._id || row.suggestedTruck))
        || '';
      // Never auto-bind multi-truck / ambiguous packets to a single unit
      if (row.matchSource === 'ambiguous') {
        truckId = row.truck || '';
      } else if (truckId && isRetiredUnit(row.suggestedUnitNumber || row.suggestedTruckLabel)) {
        truckId = '';
      } else if (!truckId && row.suggestedUnitNumber && trucks?.length) {
        // Resolve unit guess → fleet truck when API left suggestedTruck empty
        const want = String(row.suggestedUnitNumber).replace(/^0+/, '');
        const hit = trucks.find((t) => {
          if (isRetiredUnit(t.unitNumber)) return false;
          const u = String(t.unitNumber || '').trim();
          return u === String(row.suggestedUnitNumber).trim()
            || u.replace(/^0+/, '') === want;
        });
        if (hit?._id) truckId = hit._id;
      }
      return {
        ...row,
        truck: truckId ? String(truckId) : '',
        documentType: row.documentType || row.suggestedType || '',
      };
    }));
    if (data?.documentTypes?.length) setDocumentTypes(data.documentTypes);
    setSelected(new Set(
      list
        .filter((i) => {
          if (i.status === 'filed' || i.status === 'discarded' || i.status === 'pending') return false;
          if (i.matchSource === 'ambiguous') return false;
          const label = i.suggestedUnitNumber || i.suggestedTruckLabel || '';
          if (/\b(old|deactivated|retired|inactive)\b/i.test(String(label))
            || /^old[\s_-]*\d/i.test(String(label))
            || /^#?vehicle\s+\d+/i.test(String(label).trim())) {
            return false;
          }
          return (i.status === 'ready')
            || (i.confidence >= HIGH_CONFIDENCE && i.suggestedTruck && i.suggestedType);
        })
        .map((i) => String(i._id))
    ));
  }, [trucks]);

  const startPolling = useCallback((id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const data = await truckDocumentService.getSmartUploadBatch(id);
        applyBatchPayload(data);
        if (data.complete) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setStep('review');
        }
      } catch (err) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setError(err.response?.data?.message || err.message || 'Failed to load batch');
        setStep('review');
      }
    };
    setStep('processing');
    tick();
    pollRef.current = setInterval(tick, 1500);
  }, [applyBatchPayload]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setError(null);
    setSuccessMsg(null);
    setUploading(true);
    try {
      const data = await truckDocumentService.createSmartUploadBatch(files);
      setBatchId(data.batchId);
      applyBatchPayload(data);
      if (data.truncated) {
        setInfoMsg(`Split into ${data.count} page/file row(s) (capped at the upload limit). Extra pages were skipped.`);
      } else if (data.count > files.length) {
        setInfoMsg(`Expanded upload into ${data.count} rows (multi-page PDFs become one row per page; ZIP entries are listed separately).`);
      }
      startPolling(data.batchId);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Upload failed');
      setStep('upload');
    } finally {
      setUploading(false);
    }
  };

  const updateRow = (id, patch) => {
    setItems((prev) => prev.map((row) => (String(row._id) === String(id) ? { ...row, ...patch } : row)));
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectHighConfidence = () => {
    setSelected(new Set(
      items
        .filter((i) => i.status !== 'filed' && i.status !== 'discarded'
          && i.matchSource !== 'ambiguous'
          && i.confidence >= HIGH_CONFIDENCE
          && (i.truck || i.suggestedTruck)
          && (i.documentType || i.suggestedType))
        .map((i) => String(i._id))
    ));
  };

  const confirmSelected = async () => {
    if (!batchId || !selected.size) return;
    const selectedRows = items.filter((i) => selected.has(String(i._id)) && i.status !== 'filed');
    const payload = [];
    for (const i of selectedRows) {
      let truckId = i.truck || (i.matchSource === 'ambiguous' ? '' : (i.suggestedTruck?._id || i.suggestedTruck || ''));
      if (truckId && String(truckId).startsWith('samsara-')) {
        const fleetTruck = (trucks || []).find((t) => String(t._id) === String(truckId));
        try {
          const res = await apiClient.post('/trucks/ensure-from-samsara', {
            samsaraId: fleetTruck?.samsaraId || String(truckId).replace(/^samsara-/, ''),
            unitNumber: fleetTruck?.unitNumber || i.suggestedUnitNumber,
            vin: fleetTruck?.vin && fleetTruck.vin !== 'N/A' ? fleetTruck.vin : undefined,
            make: fleetTruck?.make && fleetTruck.make !== 'Unknown' ? fleetTruck.make : undefined,
            model: fleetTruck?.model && fleetTruck.model !== 'Unknown' ? fleetTruck.model : undefined,
            year: fleetTruck?.year && fleetTruck.year !== 'N/A' ? fleetTruck.year : undefined,
            type: fleetTruck?.type,
          });
          truckId = res.data?.data?._id || '';
          if (truckId) updateRow(i._id, { truck: String(truckId) });
        } catch (ensureErr) {
          setError(ensureErr.response?.data?.message || ensureErr.message || `Could not link unit for ${i.originalFilename}`);
          return;
        }
      }
      payload.push({
        _id: i._id,
        truck: truckId,
        documentType: i.documentType || i.suggestedType,
      });
    }
    const missingTruck = payload.filter((p) => !p.truck);
    if (missingTruck.length) {
      setError('Select a unit for every selected file before confirming (required for multi-truck packets).');
      return;
    }
    setConfirming(true);
    setError(null);
    try {
      const result = await truckDocumentService.confirmSmartUploadBatch(batchId, payload);
      const createdCount = result?.filed?.length || 0;
      const updatedCount = result?.updated?.length || 0;
      const errCount = result?.errors?.length || 0;
      const parts = [];
      if (createdCount) parts.push(`filed ${createdCount} new`);
      if (updatedCount) parts.push(`updated ${updatedCount} existing`);
      if (!parts.length && !errCount) parts.push('nothing to file');
      setSuccessMsg(`${parts.join(', ')}${errCount ? ` (${errCount} skipped/error${errCount === 1 ? '' : 's'})` : ''}.`);
      const refreshed = await truckDocumentService.getSmartUploadBatch(batchId);
      applyBatchPayload(refreshed);
      if ((createdCount || updatedCount) && onFiled) onFiled(result);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Confirm failed');
    } finally {
      setConfirming(false);
    }
  };

  const discardSelected = async () => {
    if (!batchId || !selected.size) return;
    setConfirming(true);
    setError(null);
    try {
      await truckDocumentService.discardSmartUploadItems(batchId, Array.from(selected));
      const refreshed = await truckDocumentService.getSmartUploadBatch(batchId);
      applyBatchPayload(refreshed);
      setSuccessMsg('Discarded selected items.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Discard failed');
    } finally {
      setConfirming(false);
    }
  };

  const pendingCount = items.filter((i) => i.status === 'pending').length;
  const actionable = items.filter((i) => i.status !== 'filed' && i.status !== 'discarded');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { minHeight: '50vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoFixHighIcon color="primary" />
          <Typography variant="h6" component="span">Smart Upload</Typography>
        </Stack>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}
        {successMsg && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>
        )}
        {infoMsg && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfoMsg(null)}>{infoMsg}</Alert>
        )}
        {items.some((i) => i.error && i.status !== 'filed') && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            AI classification issue: {items.find((i) => i.error)?.error}
          </Alert>
        )}

        {(step === 'upload' || !batchId) && (
          <Box
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              uploadFiles(e.dataTransfer.files);
            }}
            onClick={() => !uploading && inputRef.current?.click()}
            sx={{
              border: '2px dashed',
              borderColor: dragOver ? 'primary.main' : 'divider',
              bgcolor: dragOver ? 'action.hover' : 'background.paper',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: uploading ? 'wait' : 'pointer',
              mb: 2,
            }}
          >
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPT}
              hidden
              onChange={(e) => {
                uploadFiles(e.target.files);
                e.target.value = '';
              }}
            />
            {uploading ? (
              <Stack alignItems="center" spacing={1}>
                <CircularProgress size={36} />
                <Typography>Uploading…</Typography>
              </Stack>
            ) : (
              <Stack alignItems="center" spacing={1}>
                <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Drop PDF, images, DOCX, or a ZIP here
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Multi-page PDFs split into one row per page · ZIP expands to one row per file · up to 150 pages/files
                </Typography>
                <Button variant="contained" size="small" startIcon={<CloudUploadIcon />}>
                  Choose files
                </Button>
              </Stack>
            )}
          </Box>
        )}

        {step === 'processing' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Analyzing documents{pendingCount ? ` (${pendingCount} remaining)…` : '…'}
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {items.length > 0 && (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }} alignItems={{ sm: 'center' }}>
              <Typography variant="subtitle2" sx={{ flex: 1 }}>
                {actionable.length} file{actionable.length === 1 ? '' : 's'} · same truck + type updates the existing doc (filename may match)
              </Typography>
              <Button size="small" onClick={selectHighConfidence}>Select high-confidence</Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelected(new Set(actionable.map((i) => String(i._id))))}
              >
                Select all
              </Button>
            </Stack>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>File</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>Unit</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>Document type</TableCell>
                    <TableCell>Confidence</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((row) => {
                    const id = String(row._id);
                    const disabled = row.status === 'filed' || row.status === 'discarded' || row.status === 'pending';
                    return (
                      <TableRow key={id} hover selected={selected.has(id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selected.has(id)}
                            disabled={disabled}
                            onChange={() => toggleSelect(id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap title={row.originalFilename} sx={{ maxWidth: 220 }}>
                            {row.originalFilename}
                          </Typography>
                          {row.matchSource && row.matchSource !== 'none' && (
                            <Typography variant="caption" color="text.secondary">
                              via {row.matchSource}
                            </Typography>
                          )}
                          {row.error && (
                            <Typography variant="caption" color="error.main" display="block">
                              {row.error}
                            </Typography>
                          )}
                          {row.fileAction === 'update' && (
                            <Chip
                              size="small"
                              color="info"
                              label="Updates existing"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <FormControl
                            size="small"
                            fullWidth
                            disabled={disabled}
                            error={!disabled && !row.truck && (row.matchSource === 'ambiguous' || selected.has(id))}
                          >
                            <Select
                              displayEmpty
                              value={row.truck || ''}
                              onChange={(e) => updateRow(id, { truck: e.target.value, error: row.matchSource === 'ambiguous' ? undefined : row.error })}
                              renderValue={(selectedId) => {
                                if (!selectedId) {
                                  return (
                                    <em>
                                      {row.matchSource === 'ambiguous'
                                        ? 'Select unit (multi-truck file)'
                                        : row.suggestedUnitNumber
                                          ? `Select unit (AI: #${row.suggestedUnitNumber})`
                                          : 'Select unit'}
                                    </em>
                                  );
                                }
                                const t = truckOptions.find((x) => String(x._id) === String(selectedId));
                                if (t) return `#${t.unitNumber}${t.make ? ` · ${t.make}` : ''}`;
                                const label = row.suggestedTruckLabel
                                  || (row.suggestedUnitNumber ? `#${row.suggestedUnitNumber}` : '');
                                return label || 'Selected unit';
                              }}
                            >
                              <MenuItem value="">
                                <em>
                                  {row.matchSource === 'ambiguous'
                                    ? 'Select unit (multi-truck file)'
                                    : row.suggestedUnitNumber
                                      ? `Select unit (AI: #${row.suggestedUnitNumber})`
                                      : 'Select unit'}
                                </em>
                              </MenuItem>
                              {truckOptions.map((t) => (
                                <MenuItem key={t._id} value={String(t._id)}>
                                  #{t.unitNumber}
                                  {t.make ? ` · ${t.make}` : ''}
                                </MenuItem>
                              ))}
                              {/* Keep selected truck visible even if not in the filtered options list */}
                              {row.truck
                                && !truckOptions.some((t) => String(t._id) === String(row.truck))
                                && (
                                  <MenuItem value={String(row.truck)}>
                                    {row.suggestedTruckLabel || (row.suggestedUnitNumber ? `#${row.suggestedUnitNumber}` : `Unit ${String(row.truck).slice(-6)}`)}
                                  </MenuItem>
                                )}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth disabled={disabled}>
                            <Select
                              displayEmpty
                              value={row.documentType || ''}
                              onChange={(e) => updateRow(id, { documentType: e.target.value })}
                            >
                              <MenuItem value="">
                                <em>Select type</em>
                              </MenuItem>
                              {documentTypes.map((t) => (
                                <MenuItem key={t.value} value={t.value}>{t.name}</MenuItem>
                              ))}
                              {row.documentType
                                && !documentTypes.some((t) => t.value === row.documentType)
                                && (
                                  <MenuItem value={row.documentType}>{row.documentType}</MenuItem>
                                )}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>{confidenceLabel(row.confidence)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={row.status} color={statusColor(row.status)} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
        {batchId && (
          <Button color="inherit" disabled={!selected.size || confirming} onClick={discardSelected}>
            Discard selected
          </Button>
        )}
        <Button
          variant="contained"
          disabled={!selected.size || confirming || step === 'processing'}
          onClick={confirmSelected}
          startIcon={confirming ? <CircularProgress size={16} /> : null}
        >
          Confirm selected
        </Button>
      </DialogActions>
    </Dialog>
  );
}
